// src/components/VisitorsList.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plane, Fuel, Plus, Check, X, Clock, ArrowRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const VisitorsList = ({ onConvert, onRefresh }) => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [owners, setOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [converting, setConverting] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [fuelGallons, setFuelGallons] = useState('');
  const [fuelType, setFuelType] = useState('JetA');

  // Charger les visiteurs
  const fetchVisitors = async () => {
    try {
      const response = await axios.get(`${API_BASE}/tracking/visits?limit=50`);
      // S'assurer que la réponse est un tableau
      setVisitors(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching visitors:', err);
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les propriétaires pour la conversion
  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API_BASE}/owners`);
      // S'assurer que la réponse est un tableau
      setOwners(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching owners:', err);
      setOwners([]);
    }
  };

  useEffect(() => {
    fetchVisitors();
    fetchOwners();
  }, []);

  // Rafraîchir quand onRefresh change
  useEffect(() => {
    if (onRefresh) {
      fetchVisitors();
    }
  }, [onRefresh]);

  // Convertir un visiteur en aéronef enregistré
  const handleConvert = async () => {
    if (!selectedVisitor || !selectedOwnerId) return;
    
    setConverting(true);
    try {
      await axios.post(`${API_BASE}/tracking/visits/${selectedVisitor.id}/convert`, null, {
        params: { owner_id: selectedOwnerId }
      });
      
      // Fermer le modal et rafraîchir
      setShowConvertModal(false);
      setSelectedVisitor(null);
      setSelectedOwnerId('');
      fetchVisitors();
      
      if (onConvert) onConvert();
    } catch (err) {
      console.error('Error converting visitor:', err);
      alert('Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  // Ajouter du carburant à une visite
  const handleAddFuel = async () => {
    if (!selectedVisitor || !fuelGallons || parseFloat(fuelGallons) <= 0) return;
    
    try {
      await axios.post(`${API_BASE}/fuel/visitor-transaction`, {
        gallons: parseFloat(fuelGallons),
        fuel_type: fuelType,
        visit_id: selectedVisitor.id
      });
      
      setShowFuelModal(false);
      setFuelGallons('');
      fetchVisitors(); // Rafraîchir la liste
    } catch (err) {
      console.error('Error adding fuel:', err);
      alert('Erreur lors de l\'ajout de carburant: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Compter les mouvements par type
  const countMovements = (movements, type) => {
    if (!movements) return 0;
    return movements.filter(m => m.type === type).length;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-400">Loading visitors...</span>
        </div>
      </div>
    );
  }

  if (visitors.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 text-gray-400">
          <Users size={20} />
          <span>No unregistered aircraft visits yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-yellow-400" />
          <h3 className="text-white font-semibold">Aircraft Visitors (Unregistered)</h3>
          <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
            {visitors.length}
          </span>
        </div>
        <button
          onClick={fetchVisitors}
          className="text-gray-400 hover:text-white text-xs transition"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Liste des visiteurs */}
      <div className="divide-y divide-gray-700 max-h-[400px] overflow-y-auto">
        {visitors.map(visitor => (
          <div key={visitor.id} className="p-4 hover:bg-gray-700/30 transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Plane size={16} className="text-gray-400" />
                  <span className="font-mono font-bold text-white">{visitor.tail_number}</span>
                  {visitor.callsign && visitor.callsign !== visitor.tail_number && (
                    <span className="text-xs text-gray-500">({visitor.callsign})</span>
                  )}
                  {visitor.fuel_added > 0 && (
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                      ⛽ {visitor.fuel_added} gal
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">First seen</span>
                    <p className="text-gray-300">{formatDate(visitor.first_seen)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Last seen</span>
                    <p className="text-gray-300">{formatDate(visitor.last_seen)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Takeoffs</span>
                    <p className="text-green-400">{countMovements(visitor.movements, 'TAKEOFF')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Landings</span>
                    <p className="text-orange-400">{countMovements(visitor.movements, 'LANDING')}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    setSelectedVisitor(visitor);
                    setShowFuelModal(true);
                  }}
                  className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition"
                  title="Add fuel"
                >
                  <Fuel size={14} />
                </button>
                <button
                  onClick={() => {
                    setSelectedVisitor(visitor);
                    setShowConvertModal(true);
                  }}
                  className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition"
                  title="Convert to registered aircraft"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de conversion */}
      {showConvertModal && selectedVisitor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold">Convert to Registered Aircraft</h3>
              <button onClick={() => setShowConvertModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-gray-300">
                Convert <span className="font-mono text-white">{selectedVisitor.tail_number}</span> to a registered aircraft?
              </p>
              <div>
                <label className="text-gray-400 text-sm block mb-1">Select Owner</label>
                <select
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
                >
                  <option value="">-- Select owner --</option>
                  {owners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-xs">
                  ⚠️ All past movements ({countMovements(selectedVisitor.movements, 'TAKEOFF')} takeoffs, {countMovements(selectedVisitor.movements, 'LANDING')} landings) 
                  will become billable for this aircraft.
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowConvertModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConvert}
                disabled={!selectedOwnerId || converting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
              >
                {converting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Check size={16} />}
                Convert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de carburant */}
      {showFuelModal && selectedVisitor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold">Add Fuel - {selectedVisitor.tail_number}</h3>
              <button onClick={() => setShowFuelModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-gray-400 text-sm block mb-1">Fuel Type</label>
                <select
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
                >
                  <option value="100LL">100LL (Avgas)</option>
                  <option value="JetA">Jet-A</option>
                  <option value="JetA_plus">Jet-A Plus</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm block mb-1">Gallons</label>
                <input
                  type="number"
                  step="0.1"
                  value={fuelGallons}
                  onChange={(e) => setFuelGallons(e.target.value)}
                  placeholder="0.0"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowFuelModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFuel}
                disabled={!fuelGallons || parseFloat(fuelGallons) <= 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
              >
                <Fuel size={14} />
                Add Fuel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorsList;